import {
  useEffect, useMemo, useRef, 
} from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import useCurrentLoss from 'hooks/useCurrentLoss';
import useYieldLoss from 'hooks/useYieldLoss';
import useNpkLoss from 'hooks/useNpkLoss';
import useLossesOm from 'hooks/useLossesOm';
import useCurrentProfit from 'hooks/useCurrentProfit';
import useCurrency from 'hooks/useCurreny';
import {
  formatToTwoDecimals, getCellFontColor, unMaskInputValue, 
} from 'utils/helpers';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { lossesZeroTransform } from 'features/Croptimizer/utils/zeroLossTransformer';
import { formatSwissNumber } from 'utils/formatNumber';
 
interface Props {
  setProfitTitle: (value: string) => void;
  lossTitle: string;
  hasErrors?: boolean;
}

const FiveYearsBoardLoss = ({
  setProfitTitle,
  lossTitle,
  hasErrors,
}: Props) => {
  const {
    boardStatusValues,
    mineralizationValues,
    fertilizationValues,
    boardSoilValues,
    boardCropValues,
    onlyAc,
  } = useCroptimizer();

  const { t } = useTranslation();
  const { currencyGreen } = useCurrency();
  const {
    getY1CurrentLoss,
    getY2CurrentLoss,
    getY3CurrentLoss,
    getY4CurrentLoss,
    getY5CurrentLoss,
  } = useCurrentLoss({
    boardStatusValues,
    mineralizationValues,
  });

  const {
    getY1CurrentProfit,
  } = useCurrentProfit({
    boardStatusValues,
    mineralizationValues,
    boardSoilValues,
  });
  
  const {
    getY1YieldLoss,
    getY2YieldLoss,
    getY3YieldLoss,
    getY4YieldLoss,
    getY5YieldLoss,
    getTotalYieldLoss,
  } = useYieldLoss({
    boardStatusValues,
    boardCropValues,
    getY1CurrentLoss,
    getY2CurrentLoss,
    getY3CurrentLoss,
    getY4CurrentLoss,
    getY5CurrentLoss,
  });

  const {
    getY1NpkLoss,
    getY2NpkLoss,
    getY3NpkLoss,
    getY4NpkLoss,
    getY5NpkLoss,
    totalNpKLoss,
  } = useNpkLoss({
    boardStatusValues,
    getY1CurrentLoss,
    getY2CurrentLoss,
    getY3CurrentLoss,
    getY4CurrentLoss,
    getY5CurrentLoss,
    fertilizationValues,
  });

  const {
    useLossesOm1,
    useLossesOm2,
    useLossesOm3,
    useLossesOm4,
    useLossesOm5,
    totalOmLosses,
  } = useLossesOm({
    boardStatusValues,
    mineralizationValues,
    boardSoilValues,
    fertilizationValues,
    getY1CurrentProfit: getY1CurrentProfit ?? '',
    getY2CurrentProfit: getY1CurrentProfit ?? '',
    getY3CurrentProfit: getY1CurrentProfit ?? '',
    getY4CurrentProfit: getY1CurrentProfit ?? '',
    getY5CurrentProfit: getY1CurrentProfit ?? '',
    getY1CurrentLoss,
    getY2CurrentLoss,
    getY3CurrentLoss,
    getY4CurrentLoss,
    getY5CurrentLoss,
  });

  useEffect(() => {
    if (!hasErrors) {
      const title = `pH ${unMaskInputValue(getY1CurrentLoss) ?? ''}/N1 à pH ${
        unMaskInputValue(getY5CurrentLoss) ?? ''
      }/N5`;
      setProfitTitle(title);
    }
  }, [getY1CurrentLoss, getY5CurrentLoss, hasErrors, setProfitTitle]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'firstName',
        header: () => <div className='font-bold  text-black'>
          {lossTitle}
        </div>,
        size: 500,
      },
      {
        accessorKey: 'yOne',
        header: 'N1',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'yTwo',
        header: 'N2',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'yThree',
        header: 'N3',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'yFour',
        header: 'N4',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'yFive',
        header: 'N5',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div
                style={{
                  color: getCellFontColor(row.getValue()),
                  fontWeight: 700,
                }}
              >
                {row.getValue()}
              </div>
            );
          }
        },
      },
    ],
    [lossTitle],
  );

  const getTotalLosses = useMemo(
    () => [getTotalYieldLoss, totalNpKLoss, totalOmLosses].reduce(
      (acc: number, curr) => acc + (curr ? Math.abs(Number(curr)) : 0),
      0,
    ),
    [getTotalYieldLoss, totalNpKLoss, totalOmLosses],
  );

  const previousDataRef = useRef<any[] | null>(null);

  const data = useMemo(
    () => {
      // If there are errors, return previous data without recalculating
      if (hasErrors && previousDataRef.current) {
        return previousDataRef.current;
      }

      // Calculate running cumulative totals for each year column
      // Year 1 = sum of Y1 values
      const cumulativeY1 = 
        Math.abs(Number(lossesZeroTransform(getY1YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY1NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm1) || 0);
      
      // Year 2 = sum of Y1 + Y2 values
      const cumulativeY2 = 
        Math.abs(Number(lossesZeroTransform(getY1YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY1NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm1) || 0) +
        Math.abs(Number(lossesZeroTransform(getY2YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY2NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm2) || 0);
      
      // Year 3 = sum of Y1 + Y2 + Y3 values
      const cumulativeY3 = 
        Math.abs(Number(lossesZeroTransform(getY1YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY1NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm1) || 0) +
        Math.abs(Number(lossesZeroTransform(getY2YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY2NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm2) || 0) +
        Math.abs(Number(lossesZeroTransform(getY3YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY3NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm3) || 0);
      
      // Year 4 = sum of Y1 + Y2 + Y3 + Y4 values
      const cumulativeY4 = 
        Math.abs(Number(lossesZeroTransform(getY1YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY1NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm1) || 0) +
        Math.abs(Number(lossesZeroTransform(getY2YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY2NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm2) || 0) +
        Math.abs(Number(lossesZeroTransform(getY3YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY3NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm3) || 0) +
        Math.abs(Number(lossesZeroTransform(getY4YieldLoss)) || 0) + 
        Math.abs(Number(lossesZeroTransform(getY4NpkLoss)) || 0) + 
        Math.abs(Number(useLossesOm4) || 0);
      
      // Year 5 = same as total (use getTotalLosses directly to avoid rounding discrepancies)
      const cumulativeY5 = getTotalLosses;

      // Calculate new data
      const newData = [
        {
          firstName: t('soilPhEvolution'),
          yOne: formatToTwoDecimals(unMaskInputValue(getY1CurrentLoss)),
          yTwo: formatToTwoDecimals(unMaskInputValue(getY2CurrentLoss)),
          yThree: formatToTwoDecimals(unMaskInputValue(getY3CurrentLoss)),
          yFour: formatToTwoDecimals(unMaskInputValue(getY4CurrentLoss)),
          yFive: formatToTwoDecimals(unMaskInputValue(getY5CurrentLoss)),
          total: '-',
        },

        {
          firstName: t('yieldLosses') + ` (${currencyGreen}/${onlyAc})`,
          yOne: lossesZeroTransform(getY1YieldLoss),
          yTwo: lossesZeroTransform(getY2YieldLoss),
          yThree: lossesZeroTransform(getY3YieldLoss),
          yFour: lossesZeroTransform(getY4YieldLoss),
          yFive: lossesZeroTransform(getY5YieldLoss),
          total: formatSwissNumber(
            Number(lossesZeroTransform(getTotalYieldLoss)),
          ),
        },

        {
          firstName: t('npkLosses') + ` (${currencyGreen}/${onlyAc})`,
          yOne: lossesZeroTransform(getY1NpkLoss),
          yTwo: lossesZeroTransform(getY2NpkLoss),
          yThree: lossesZeroTransform(getY3NpkLoss),
          yFour: lossesZeroTransform(getY4NpkLoss),
          yFive: lossesZeroTransform(getY5NpkLoss),
          total: formatSwissNumber(Number(lossesZeroTransform(totalNpKLoss))),
        },

        {
          firstName: t('lossesmonps') + ` (${currencyGreen}/${onlyAc})`,
          yOne: Math.round(Number(useLossesOm1)),
          yTwo: Math.round(Number(useLossesOm2)),
          yThree: Math.round(Number(useLossesOm3)),
          yFour: Math.round(Number(useLossesOm4)),
          yFive: Math.round(Number(useLossesOm5)),
          total: formatSwissNumber(Number(totalOmLosses)),
        },

        {
          firstName: t('totalLosses') + ` (${currencyGreen}/${onlyAc})`,
          yOne: formatSwissNumber(-cumulativeY1),
          yTwo: formatSwissNumber(-cumulativeY2),
          yThree: formatSwissNumber(-cumulativeY3),
          yFour: formatSwissNumber(-cumulativeY4),
          yFive: formatSwissNumber(-cumulativeY5),
          total: formatSwissNumber(Number(lossesZeroTransform(getTotalLosses))),
        },
      ];

      // Save new data for future use
      previousDataRef.current = newData;

      return newData;
    },
    // Early return prevents calculations from running when hasErrors
    [
      hasErrors,
      getY1CurrentLoss,
      getY2CurrentLoss,
      getY3CurrentLoss,
      getY4CurrentLoss,
      getY5CurrentLoss,
      currencyGreen,
      onlyAc,
      getY1YieldLoss,
      getY2YieldLoss,
      getY3YieldLoss,
      getY4YieldLoss,
      getY5YieldLoss,
      getTotalYieldLoss,
      getY1NpkLoss,
      getY2NpkLoss,
      getY3NpkLoss,
      getY4NpkLoss,
      getY5NpkLoss,
      totalNpKLoss,
      useLossesOm1,
      useLossesOm2,
      useLossesOm3,
      useLossesOm4,
      useLossesOm5,
      totalOmLosses,
      getTotalLosses,
      t,
    ],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 100,
    },
  });

  return (
    <div>
      <div className="text-black font-bold text-lg md:text-base md:mt-6 mb-4">
        {t('potentialLossesFiveYears')}
      </div>

      <div className="overflow-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="text-left">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="border-b border-b-gray-200 py-4 px-1 text-[#757575] font-normal text-[16px]"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="border-none">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="text-left">
                {row.getVisibleCells().map((cell) => (
                  <td
                    style={{
                      width: cell.column.getSize(),
                    }}
                    key={cell.id}
                    className="border-b border-b-gray-200 py-4 px-1 text-[16px]"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="h-4" />
      </div>
    </div>
  );
};

export default FiveYearsBoardLoss;
